import { useEffect, useState } from "react";
import { api, type PublicUser } from "../api";
import { Banner, Card, CardHead, Pill, Skeleton, Toasts, useToasts } from "../components/ui";
import { IconCheck } from "../components/icons";

const ROLES: PublicUser["role"][] = ["admin", "trader", "recon", "viewer"];

const ROLE_HELP: Record<PublicUser["role"], string> = {
  admin: "Full access, including approvals, pricing and user management.",
  trader: "Desk operations and execution. No approvals or user management.",
  recon: "Reconciliation. Can acknowledge and resolve breaks.",
  viewer: "Read-only across the desk.",
};

export function Users() {
  const [users, setUsers] = useState<PublicUser[] | null>(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<PublicUser["role"]>("viewer");
  const [tempPassword, setTempPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToasts();

  async function refresh() {
    const r = await api.usersList();
    setUsers(r.users);
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setTempPassword("");
    setBusy(true);
    try {
      const r = await api.userCreate(email, name, role);
      setTempPassword(r.temporary_password);
      setEmail("");
      setName("");
      await refresh();
    } catch {
      setError("Could not create the user. Is that email already registered?");
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(u: PublicUser, newRole: PublicUser["role"]) {
    try {
      await api.userSetRole(u.id, newRole);
      toast.push("ok", `${u.displayName} is now ${newRole}.`);
    } catch {
      toast.push("error", "Role change failed.");
    }
    await refresh();
  }

  async function toggleStatus(u: PublicUser) {
    const next = u.status === "active" ? "disabled" : "active";
    try {
      await api.userSetStatus(u.id, next);
      toast.push("ok", `${u.displayName} ${next === "active" ? "enabled" : "disabled"}.`);
    } catch {
      toast.push("error", "Status change failed.");
    }
    await refresh();
  }

  if (!users) return <Skeleton rows={5} />;

  return (
    <>
      <p className="page-intro">
        Desk operators and their access. Every new account is created with a one-time password and must enrol
        an authenticator before it can be used.
      </p>

      <Card pad={false} className="enter">
        <CardHead title="Add an operator" />
        <form className="card-pad" onSubmit={create}>
          <div className="row" style={{ flexWrap: "wrap" }}>
            <div className="field" style={{ margin: 0, minWidth: 200 }}>
              <label htmlFor="new-email">Email</label>
              <input id="new-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="field" style={{ margin: 0, minWidth: 180 }}>
              <label htmlFor="new-name">Display name</label>
              <input id="new-name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="field" style={{ margin: 0, minWidth: 150 }}>
              <label htmlFor="new-role">Role</label>
              <select id="new-role" value={role} onChange={(e) => setRole(e.target.value as PublicUser["role"])}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="shrink">
              <button className="btn btn-primary" disabled={busy}>{busy ? "Creating" : "Create user"}</button>
            </div>
          </div>
          <div className="field-hint" style={{ marginTop: 10 }}>{ROLE_HELP[role]}</div>

          {tempPassword && (
            <div style={{ marginTop: 16 }}>
              <Banner tone="ok">
                <div style={{ fontWeight: 500, marginBottom: 6 }}>
                  Temporary password. Shown once, so hand it over securely.
                </div>
                <div className="mono" style={{ fontSize: 15, letterSpacing: "0.02em", color: "var(--text)" }}>
                  {tempPassword}
                </div>
              </Banner>
            </div>
          )}
          {error && <div style={{ marginTop: 16 }}><Banner tone="error">{error}</Banner></div>}
        </form>
      </Card>

      <div className="section">
        <Card pad={false}>
          <CardHead title={`Operators (${users.length})`} />
          <div className="table-wrap flush">
            <table>
              <thead>
                <tr><th>Operator</th><th>Role</th><th>Two-factor</th><th>Status</th><th className="right" /></tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{u.displayName}</div>
                      <div className="dim t-sm">{u.email}</div>
                    </td>
                    <td>
                      <select
                        value={u.role}
                        aria-label={`Role for ${u.displayName}`}
                        onChange={(e) => void changeRole(u, e.target.value as PublicUser["role"])}
                        style={{ width: "auto", height: 30, fontSize: "var(--text-sm)" }}
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td>
                      {u.totpEnabled
                        ? <span className="gain" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <IconCheck size={14} /> enabled
                          </span>
                        : <span className="warn">enrolment pending</span>}
                    </td>
                    <td>
                      <Pill tone={u.status === "active" ? "good" : "neutral"} dot>{u.status}</Pill>
                    </td>
                    <td className="right">
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => void toggleStatus(u)}>
                        {u.status === "active" ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Toasts items={toast.items} onDone={toast.dismiss} />
    </>
  );
}
